'use client';
import Link from 'next/link';
import { useState, useEffect, useCallback, useRef } from 'react';
import type { MouseEvent } from 'react';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import Image from 'next/image';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScroll = useRef(0);
  const scrollToSection = useCallback((hash: string) => {
    const target = document.querySelector(hash);
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleNavClick = useCallback(
    (hash: string, onAfter?: () => void) =>
      (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        event.preventDefault();
        scrollToSection(hash);
        if (onAfter) onAfter();
      },
    [scrollToSection],
  );
  useEffect(() => {
    const onScroll = () => {
      const current = window.scrollY || document.documentElement.scrollTop;
      setScrolled(current > 4);

      if (open || current <= 80) {
        setHidden(false);
        lastScroll.current = current;
        return;
      }

      const isScrollingDown = current > lastScroll.current;
      setHidden(isScrollingDown);
      lastScroll.current = current;
    };

    lastScroll.current = window.scrollY || document.documentElement.scrollTop;
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [open]);

  useEffect(() => {
    if (open) {
      setHidden(false);
    }
  }, [open]);

  return (
    <header
      className={`bg-hero-gradient sticky top-0 z-30 transition-transform duration-300 ease-out ${hidden ? '-translate-y-full' : 'translate-y-0'}`}
      style={{
        backdropFilter: 'saturate(180%) blur(6px)',
        borderBottom: scrolled ? '1px solid hsl(var(--border))' : '1px solid transparent',
      }}
    >
      <div className="container flex h-16 items-center justify-between">
        {/* Brand */}
        <Link href="/" className="flex items-center gap-2">
          {/* <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary font-extrabold text-white">
            R
          </div> */}
          <Image
            src="/logo.svg"
            alt="Reparoly"
            width={58}
            height={58}
            className="h-9 w-9 rounded-md"
          />
          <span className="font-extrabold">Reparoly</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex">
          <a
            className="hover:text-primary"
            href="#beneficios"
            onClick={handleNavClick('#beneficios')}
          >
            Producto
          </a>
          <a
            className="hover:text-primary"
            href="#como-funciona"
            onClick={handleNavClick('#como-funciona')}
          >
            Cómo funciona
          </a>
          <a className="hover:text-primary" href="#precios" onClick={handleNavClick('#precios')}>
            Precios
          </a>
          <a className="hover:text-primary" href="#faq" onClick={handleNavClick('#faq')}>
            FAQ
          </a>
        </nav>

        {/* Desktop actions */}
        <div className="hidden items-center gap-2 md:flex">
          <Button asChild variant="ghost">
            <Link href="/auth">Entrar</Link>
          </Button>
          <Button asChild className="rounded-full">
            <Link href="#precios" onClick={handleNavClick('#precios')}>
              Probar gratis
            </Link>
          </Button>
        </div>

        {/* Mobile menu toggle */}
        <button
          className="inline-flex items-center justify-center rounded-md p-2 md:hidden"
          aria-label="Abrir menú"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile panel (simple, debajo del header) */}
      <div className={`${open ? 'grid' : 'hidden'} border-t border-[hsl(var(--border))] md:hidden`}>
        <div className="container py-3">
          <nav className="grid gap-3 py-2 text-base">
            <a
              className="py-1"
              href="#beneficios"
              onClick={handleNavClick('#beneficios', () => setOpen(false))}
            >
              Producto
            </a>
            <a
              className="py-1"
              href="#como-funciona"
              onClick={handleNavClick('#como-funciona', () => setOpen(false))}
            >
              Cómo funciona
            </a>
            <a
              className="py-1"
              href="#precios"
              onClick={handleNavClick('#precios', () => setOpen(false))}
            >
              Precios
            </a>
            <a className="py-1" href="#faq" onClick={handleNavClick('#faq', () => setOpen(false))}>
              FAQ
            </a>
          </nav>
          <div className="grid grid-cols-2 gap-2 pb-4">
            <Button asChild variant="ghost">
              <Link href="/auth" onClick={() => setOpen(false)}>
                Entrar
              </Link>
            </Button>
            <Button asChild className="rounded-full">
              <Link href="/signup" onClick={handleNavClick('#precios', () => setOpen(false))}>
                Probar gratis
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
